import { NavLink } from "react-router-dom";
import { Activity, KanbanSquare, LayoutDashboard, LogOut, ShieldCheck, Table2, TrendingUp, UserCircle, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SampleDataButton } from "./SampleDataButton";
import { LeadEditor } from "./LeadEditor";
import { LossReasonDialog } from "./LossReasonDialog";

const NAV = [
  { to: "/", label: "Board", icon: KanbanSquare, testid: "nav-board" },
  { to: "/leads", label: "Lead Log", icon: Table2, testid: "nav-lead-log" },
  { to: "/activity", label: "Activity", icon: Activity, testid: "nav-activity" },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/projection", label: "Projection", icon: TrendingUp, testid: "nav-projection" },
  { to: "/team", label: "Team", icon: Users, testid: "nav-team" },
];
const ADMIN_NAV = { to: "/users", label: "Users", icon: ShieldCheck, testid: "nav-users" };

const UserMenu = () => {
  const { user, logout } = useAuth();
  return (
    <div className="flex min-w-0 items-center gap-1.5" data-testid="header-user">
      <NavLink to="/account" data-testid="nav-account" title="Account"
        className={({ isActive }) => `nav-link min-w-0 ${isActive ? "nav-link-active" : ""}`}>
        <UserCircle className="h-4 w-4 shrink-0" />
        <span data-testid="header-user-name" className="max-w-[9rem] truncate text-slate-100">{user.name}</span>
        <span data-testid="header-user-role" className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${user.role === "Admin" ? "bg-indigo-500/20 text-indigo-300" : "bg-slate-800 text-slate-300"}`}>{user.role}</span>
      </NavLink>
      <button type="button" data-testid="header-logout-btn" onClick={logout} className="btn-ghost h-9 shrink-0 px-2.5">
        <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Log out</span>
      </button>
    </div>
  );
};

export const Layout = ({ children }) => {
  const { user } = useAuth();
  const nav = user.role === "Admin" ? [...NAV, ADMIN_NAV] : NAV;
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-[#0B0F17]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1800px] flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2.5" data-testid="app-brand">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-indigo-600 font-display text-sm font-extrabold text-white">AS</span>
            <span className="font-display text-base font-extrabold tracking-tight">Ace Sales CRM</span>
          </div>
          <nav className="order-3 -mx-1 flex w-full gap-1 overflow-x-auto sm:order-none sm:w-auto" data-testid="main-nav">
            {nav.map(({ to, label, icon: Icon, testid }) => (
              <NavLink key={to} to={to} end data-testid={testid}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}>
                <Icon className="h-4 w-4" /> {label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
            <SampleDataButton />
            <UserMenu />
          </div>
        </div>
      </header>
      <main className="mx-auto min-w-0 max-w-[1800px] px-4 py-6 sm:px-6">{children}</main>
      <LeadEditor />
      <LossReasonDialog />
    </div>
  );
};
